import { Component, Project } from "projen";
import { GitHub, GithubWorkflow } from "projen/lib/github";
import { Job, JobPermission } from "projen/lib/github/workflows-model";
import {
  PLAN_RUN_ID_INPUT,
  VALIDATE_PLAN_JOB_ID,
  renderPlanValidationScript,
} from "./plan-apply";
import { AwsDeploymentTarget } from "../aws/aws-deployment-target";

/**
 * Settings every `AwsDeployWorkflow` sharing one apply workflow must agree on,
 * because they are baked into the single shared `validate-plan` job.
 */
export interface ApplyWorkflowContract {
  /**
   * Name of the plan (build) workflow whose runs this apply workflow accepts.
   */
  readonly planWorkflowName: string;

  /**
   * Assert the plan run's commit is still the head of its branch.
   */
  readonly requireCurrentHead: boolean;

  /**
   * Resolve and export the build artifact's digest during validation.
   */
  readonly verifyArtifactDigest: boolean;
}

/**
 * Inputs to {@link ApplyWorkflow.attach}.
 */
export interface ApplyWorkflowAttachOptions extends ApplyWorkflowContract {
  /**
   * Targets the attaching component deploys. Their branches join the union the
   * validation job accepts.
   */
  readonly awsDeploymentTargets: Array<AwsDeploymentTarget>;

  /**
   * Component name used in thrown error messages.
   */
  readonly componentName: string;
}

/**
 * Inputs to the {@link ApplyWorkflow} constructor.
 */
export interface ApplyWorkflowOptions extends ApplyWorkflowAttachOptions {
  /**
   * File name (without `.yml`) of the generated apply workflow.
   */
  readonly applyWorkflowName: string;

  /**
   * Rendered repository-guard expression for the validation job, if any.
   */
  readonly homeRepositoryCondition?: string;
}

/**
 * The dispatch-only apply workflow behind the `plan-apply` gate, plus the state
 * every `AwsDeployWorkflow` attached to it shares.
 *
 * One component owns the file so several deploy components — typically one per
 * service in a multi-service repo, all sharing a build workflow — can put their
 * apply jobs in it. That is what lets a cross-service `deployAfterTargets`
 * graph render as valid `needs`: GitHub resolves `needs` only within a single
 * workflow file.
 *
 * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
 */
export class ApplyWorkflow extends Component {
  public static of(
    project: Project,
    workflow: GithubWorkflow,
  ): ApplyWorkflow | undefined {
    const isDefined = (c: Component): c is ApplyWorkflow =>
      c instanceof ApplyWorkflow && c.workflow === workflow;
    return project.components.find(isDefined);
  }

  /**
   * The generated workflow. Exposed so a second `AwsDeployWorkflow` can be
   * pointed at it via `planApply.applyWorkflow`.
   */
  public readonly workflow: GithubWorkflow;

  /**
   * Settings every attached component must agree on.
   */
  public readonly contract: ApplyWorkflowContract;

  /**
   * Repository guard on the validation job, if any.
   */
  private readonly homeRepositoryCondition?: string;

  /**
   * Every attached component's targets, in attach order. Held as targets rather
   * than branches because `AwsDeploymentTarget.branches` is mutable — the
   * branch union is read at `preSynthesize`, once every component has attached.
   */
  private readonly attachedTargets: Array<AwsDeploymentTarget> = [];

  constructor(project: Project, github: GitHub, options: ApplyWorkflowOptions) {
    super(project);

    const { applyWorkflowName } = options;

    /**
     * A name already in use would silently collide on the underlying
     * `.github/workflows/<name>.yml` file. The usual cause is two components
     * that each create an apply workflow while sharing one build workflow; the
     * fixes are `planApply.applyWorkflow` to share this one, or
     * `planApply.applyWorkflowName` to create a second under its own name.
     */
    if (github.tryFindWorkflow(applyWorkflowName)) {
      throw new Error(
        `${options.componentName} cannot create the apply workflow "${applyWorkflowName}" because a workflow with that name already exists. Set \`planApply.applyWorkflow\` to share the existing one, or \`planApply.applyWorkflowName\` to something unique.`,
      );
    }

    this.contract = {
      planWorkflowName: options.planWorkflowName,
      requireCurrentHead: options.requireCurrentHead,
      verifyArtifactDigest: options.verifyArtifactDigest,
    };
    this.homeRepositoryCondition = options.homeRepositoryCondition;
    this.attachedTargets.push(...options.awsDeploymentTargets);

    this.workflow = new GithubWorkflow(github, applyWorkflowName);

    this.workflow.on({
      workflowDispatch: {
        inputs: {
          [PLAN_RUN_ID_INPUT]: {
            description:
              "Run id of the plan workflow run to apply. Its build artifact is deployed verbatim.",
            required: true,
            type: "string",
          },
        },
      },
    });

    /**
     * Registered here rather than at `preSynthesize` so the validation job
     * keeps its position ahead of every apply job in the rendered file.
     * `preSynthesize` replaces it in place once the branch union is final.
     */
    this.workflow.addJob(VALIDATE_PLAN_JOB_ID, this.renderValidateJob());
  }

  /**
   * Attach another component's apply jobs to this workflow.
   *
   * Every setting the shared `validate-plan` job bakes in must match what the
   * workflow was created with — there is one validation job for the whole file,
   * so a disagreement cannot be honoured and is rejected rather than resolved
   * in favour of whichever component happened to be constructed first.
   */
  public attach = (options: ApplyWorkflowAttachOptions): void => {
    const { componentName } = options;
    const name = this.workflow.name;

    if (options.planWorkflowName !== this.contract.planWorkflowName) {
      throw new Error(
        `${componentName} cannot attach to the apply workflow "${name}": it plans from build workflow "${options.planWorkflowName}", but that apply workflow only accepts runs of "${this.contract.planWorkflowName}". Every component sharing an apply workflow must share one build workflow.`,
      );
    }

    this.requireAgreement(
      componentName,
      "requireCurrentHead",
      options.requireCurrentHead,
      this.contract.requireCurrentHead,
    );
    this.requireAgreement(
      componentName,
      "verifyArtifactDigest",
      options.verifyArtifactDigest,
      this.contract.verifyArtifactDigest,
    );

    this.attachedTargets.push(...options.awsDeploymentTargets);
  };

  preSynthesize(): void {
    /**
     * Re-rendered now that every component has attached, so `allowedBranches`
     * covers the union of all of their targets. `updateJob` replaces the job in
     * place, leaving its position — and a single-component workflow's rendered
     * YAML — unchanged.
     */
    this.workflow.updateJob(VALIDATE_PLAN_JOB_ID, this.renderValidateJob());

    super.preSynthesize();
  }

  /**
   * Reject a boolean `planApply` setting that differs from the one this
   * workflow was created with.
   */
  private requireAgreement = (
    componentName: string,
    option: string,
    supplied: boolean,
    expected: boolean,
  ): void => {
    if (supplied === expected) {
      return;
    }

    throw new Error(
      `${componentName} cannot attach to the apply workflow "${this.workflow.name}": \`planApply.${option}\` is ${supplied} here and ${expected} on the components already attached. The setting is baked into the one shared \`${VALIDATE_PLAN_JOB_ID}\` job, so every component sharing an apply workflow must agree on it.`,
    );
  };

  /**
   * Render the validation job every apply job depends on.
   *
   * `allowedBranches` is the union of every attached target's branches, deduped
   * and left in attach order. The apply run's own `github.ref` says nothing
   * about what was planned, so the list is checked against the *plan run's*
   * `head_branch` rather than against a workflow-level branch filter.
   */
  private renderValidateJob = (): Job => {
    const { planWorkflowName, requireCurrentHead, verifyArtifactDigest } =
      this.contract;

    const allowedBranches = Array.from(
      new Set(
        this.attachedTargets.flatMap((target) =>
          target.branches.map((b) => b.branch),
        ),
      ),
    );

    return {
      name: "Validate plan run",
      runsOn: ["ubuntu-latest"],
      permissions: {
        actions: JobPermission.READ,
        contents: JobPermission.READ,
      },
      ...(this.homeRepositoryCondition
        ? { if: `\${{ ${this.homeRepositoryCondition} }}` }
        : {}),
      outputs: {
        head_sha: { stepId: "validate_plan_run", outputName: "head_sha" },
        head_branch: { stepId: "validate_plan_run", outputName: "head_branch" },
        ...(verifyArtifactDigest
          ? {
              artifact_digest: {
                stepId: "validate_plan_run",
                outputName: "artifact_digest",
              },
            }
          : {}),
      },
      steps: [
        {
          name: "Validate plan run",
          id: "validate_plan_run",
          uses: "actions/github-script@v9",
          /**
           * The dispatch input reaches the script through the environment
           * rather than a `${{ }}` interpolation, so a crafted run id cannot
           * inject JavaScript into the validation itself.
           */
          env: {
            PLAN_RUN_ID: `\${{ inputs.${PLAN_RUN_ID_INPUT} }}`,
          },
          with: {
            script: renderPlanValidationScript({
              planWorkflowPath: `.github/workflows/${planWorkflowName}.yml`,
              allowedBranches,
              requireCurrentHead,
              verifyArtifactDigest,
            }),
          },
        },
      ],
    };
  };
}
